<template>
  <div class="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="lecturer-courses-widget">
    <div class="px-3 py-2.5 border-b border-gray-100 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-2">
      <div>
        <h3 class="text-sm font-semibold text-gray-800">Lecturer Courses</h3>
        <p class="text-xs text-gray-500 mt-0.5">Manage course allocations and approvals</p>
      </div>
      <div class="flex items-center gap-2">
        <div class="w-60">
          <AcademicCalendarSelect
            v-model="selectedAcademicCalendar"
            placeholder="Select Academic Calendar"
            class="w-full"
            data-testid="lecturer-courses-calendar-select"
          />
        </div>
        <Button v-if="canUpdate" label="Assign Courses" icon="pi pi-plus" size="small"
          :disabled="!selectedAcademicCalendar" @click="showAssignDialog = true" data-testid="btn-assign-courses" />
      </div>
    </div>
    <div class="p-3">
      <template v-if="loading">
        <div class="flex items-center justify-center py-6">
          <span class="pi pi-spin pi-spinner text-lg text-primary"></span>
        </div>
      </template>

      <!-- Bulk Action Bar -->
      <div v-if="selectedCourses.length > 0" class="flex items-center gap-2 px-3 py-2 mb-2 bg-primary/10 border border-primary/20 rounded-lg">
        <span class="text-xs font-semibold text-primary-700">{{ selectedCourses.length }} course{{ selectedCourses.length > 1 ? 's' : '' }} selected</span>
        <div class="flex-1"></div>
        <Button label="Approve" icon="pi pi-check" size="small" severity="success" outlined
          :loading="bulkLoading" @click="bulkAction(true)" data-testid="btn-bulk-approve" />
        <Button label="Deny" icon="pi pi-times" size="small" severity="danger" outlined
          :loading="bulkLoading" @click="bulkAction(false)" data-testid="btn-bulk-deny" />
        <Button icon="pi pi-filter-slash" text rounded size="small" severity="secondary"
          @click="selectedCourses = []" v-tooltip.top="'Clear'" />
      </div>

      <template v-if="!loading && filteredCourses.length">
        <DataTable
          :value="filteredCourses"
          v-model:selection="selectedCourses"
          dataKey="course_id"
          responsiveLayout="scroll"
          :paginator="true"
          :rows="10"
          size="small"
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          :rowsPerPageOptions="[5, 10, 20, 50]"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} courses"
          class="p-datatable-sm"
          data-testid="lecturer-courses-table"
        >
          <Column selectionMode="multiple" headerStyle="width: 3rem" />
          <Column field="course.course_code" header="Course Code" style="width: 12%">
            <template #body="{ data }">
              <span class="font-semibold">{{ data.course.course_code }}</span>
            </template>
          </Column>
          <Column field="course.course_title" header="Course Title" style="width: 22%">
            <template #body="{ data }">{{ data.course.course_title }}</template>
          </Column>
          <Column field="course.course_instance_id" header="Course Instance" style="width: 18%">
            <template #body="{ data }">{{ formatCourseInstance(data.course) || '—' }}</template>
          </Column>
          <Column field="course.unit" header="Unit(s)" style="width: 8%">
            <template #body="{ data }">
              <Badge :value="`${data.course.unit}`" severity="info" />
            </template>
          </Column>
          <Column header="Status" style="width: 12%">
            <template #body="{ data }">
              <Tag v-if="data.is_approved" value="Approved" severity="success" icon="pi pi-check" />
              <Tag v-else-if="data.denied_at" value="Denied" severity="danger" icon="pi pi-times" />
              <Tag v-else value="Pending" severity="warning" icon="pi pi-clock" />
            </template>
          </Column>
          <Column v-if="canUpdate" header="Actions" style="width: 28%">
            <template #body="{ data }">
              <div class="flex gap-1.5">
                <Button v-if="!data.is_approved" icon="pi pi-check" size="small" severity="success"
                  v-tooltip.top="'Approve'" :loading="actionLoading" @click="openConfirmDialog(data.course_id, 'approve')" data-testid="btn-approve-course" />
                <Button v-if="data.is_approved" icon="pi pi-times" size="small" severity="danger"
                  v-tooltip.top="'Deny'" :loading="actionLoading" @click="openConfirmDialog(data.course_id, 'deny')" data-testid="btn-deny-course" />
                <Button icon="pi pi-share-alt" size="small" severity="info" class="p-button-outlined"
                  v-tooltip.top="'Allocate to another lecturer'" @click="openAllocateDialog(data.course_id)" data-testid="btn-allocate-course" />
                <Button icon="pi pi-minus-circle" size="small" severity="warn" class="p-button-outlined"
                  v-tooltip.top="'Deallocate'" :loading="allocating" @click="openConfirmDialog(data.course_id, 'deallocate')" data-testid="btn-deallocate-course" />
              </div>
            </template>
          </Column>
        </DataTable>
      </template>

      <template v-else>
        <div class="text-center py-8" data-testid="lecturer-courses-empty">
          <i class="pi pi-inbox text-2xl text-gray-400 mb-2"></i>
          <p class="text-gray-500 text-sm">
            <span v-if="selectedAcademicCalendar">No courses found for this academic calendar.</span>
            <span v-else>Please select an academic calendar to view courses.</span>
          </p>
        </div>
      </template>
    </div>

    <!-- Allocate Dialog -->
    <Dialog v-model:visible="showAllocateDialog" modal :dismissableMask="true" :style="{ width: '440px' }" :pt="{ root: { class: 'rounded-xl overflow-hidden' } }">
      <template #header>
        <div class="flex items-center gap-2">
          <div class="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10">
            <i class="pi pi-share-alt text-primary text-sm"></i>
          </div>
          <div>
            <h3 class="font-semibold text-gray-800 text-sm">Allocate Course</h3>
            <p class="text-xs text-gray-500">Assign this course to another lecturer</p>
          </div>
        </div>
      </template>
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-gray-600">Target Lecturer *</label>
        <Select
          v-model="targetLecturer"
          :options="lecturers"
          optionLabel="name"
          placeholder="Select lecturer to allocate to"
          filter
          class="w-full"
          data-testid="allocate-lecturer-dropdown"
        />
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <Button label="Cancel" severity="secondary" text size="small" @click="showAllocateDialog = false" />
          <Button label="Allocate" icon="pi pi-check" size="small" :loading="allocating" :disabled="!targetLecturer" @click="handleAllocate()" data-testid="btn-confirm-allocate" />
        </div>
      </template>
    </Dialog>

    <!-- Confirm Dialog -->
    <Dialog v-model:visible="showConfirmDialog" modal :dismissableMask="true" :style="{ width: '400px' }" :pt="{ root: { class: 'rounded-xl overflow-hidden' } }">
      <template #header>
        <div class="flex items-center gap-2">
          <div class="flex items-center justify-center w-8 h-8 rounded-full" :class="confirmAction === 'approve' ? 'bg-primary/10' : confirmAction === 'deny' ? 'bg-red-50' : 'bg-orange-50'">
            <i :class="['pi text-sm', confirmAction === 'approve' ? 'pi-check text-primary' : confirmAction === 'deny' ? 'pi-times text-red-600' : 'pi-minus-circle text-orange-600']"></i>
          </div>
          <div>
            <h3 class="font-semibold text-gray-800 text-sm capitalize">{{ confirmActionText }} Course</h3>
            <p class="text-xs text-gray-500">Please confirm this action</p>
          </div>
        </div>
      </template>
      <div class="py-1.5">
        <p class="text-sm text-gray-700">
          Are you sure you want to <span class="font-semibold capitalize">{{ confirmActionText }}</span> this course? This action will take effect immediately.
        </p>
      </div>
      <template #footer>
        <div class="flex justify-end gap-2">
          <Button label="Cancel" severity="secondary" text size="small" @click="showConfirmDialog = false" />
          <Button label="Confirm" size="small" :severity="confirmAction === 'approve' ? 'success' : confirmAction === 'deny' ? 'danger' : 'warn'" @click="handleConfirmAction" data-testid="btn-confirm-action" />
        </div>
      </template>
    </Dialog>

    <!-- Assign Courses Dialog -->
    <Dialog v-model:visible="showAssignDialog" modal header="Assign Courses to Lecturer" class="w-full max-w-lg">
      <div class="space-y-3">
        <p class="text-sm text-gray-500">Search and select courses to assign to this lecturer.</p>
        <InputText v-model="courseSearchQuery" placeholder="Search by code or title..." class="w-full" size="small" />
        <div class="max-h-64 overflow-y-auto border rounded-lg">
          <div v-if="courseSearchLoading" class="flex justify-center py-4">
            <i class="pi pi-spin pi-spinner text-gray-400"></i>
          </div>
          <div v-else-if="availableCourses.length === 0" class="text-center py-4 text-xs text-gray-400">
            {{ courseSearchQuery ? 'No courses found' : 'Type to search courses' }}
          </div>
          <div v-else>
            <label v-for="course in availableCourses" :key="course.id"
              class="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-50 last:border-0">
              <Checkbox v-model="assignSelectedIds" :value="Number(course.id)" />
              <span class="text-sm font-medium">{{ course.course_code }}</span>
              <span class="text-xs text-gray-500 truncate">{{ course.course_title }}</span>
              <Badge :value="`${course.unit}u`" severity="info" class="ml-auto" />
            </label>
          </div>
        </div>
        <p v-if="assignSelectedIds.length" class="text-xs text-primary font-medium">{{ assignSelectedIds.length }} course(s) selected</p>
      </div>
      <template #footer>
        <Button label="Cancel" severity="secondary" text size="small" @click="showAssignDialog = false" />
        <Button label="Assign" icon="pi pi-plus" size="small"
          :loading="assigning" :disabled="!assignSelectedIds.length" @click="handleAssignCourses" />
      </template>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import AcademicCalendarSelect from '@/components/common/AcademicCalendarSelect.vue';
import { useToast } from 'primevue/usetoast';
import { useQuery, useLazyQuery } from '@vue/apollo-composable';
import { GET_COURSES } from '@/graphql/Queries/tertiary';
import { debounce } from '@/utils';
import { formatCourseInstance } from '@/utils/courseInstance';
import type { Staff, CourseLecturer } from '@/types/composition-functions';
import { useApproveOrDenyCourseMutation, useAllocateCoursesToLecturerMutation, useDeallocateCourseFromLecturerMutation } from '@/types/vue-apollo-helpers';
import { GET_STAFF_BY_ID } from '@/graphql';
import { useStaff } from '@/composables/queries/useStaff';
import { can, AppPermissions } from '@/utils/permissions';
import { useUserStore } from '@/store/userStore';
import { storeToRefs } from 'pinia';

const props = defineProps<{
  staffId: string;
}>();

const toast = useToast();
const userStore = useUserStore();
const { currentUser } = storeToRefs(userStore);

const canUpdate = computed(() => can(currentUser.value, AppPermissions.LecturerCourseAllocation.Actions.Update, AppPermissions.LecturerCourseAllocation.Resource));

const selectedAcademicCalendar = ref<string | number | null>(null);

const {
  result: staffResult,
  loading,
  refetch,
} = useQuery(
  GET_STAFF_BY_ID,
  () => ({ id: props.staffId }),
  { fetchPolicy: 'network-only' },
);

const teacher = computed<Staff | null>(() => (staffResult.value?.staff as Staff) ?? null);

watch(() => props.staffId, () => {
  selectedAcademicCalendar.value = null;
});

const filteredCourses = computed(() => {
  if (!teacher.value?.lecturer_courses_in_semester || !selectedAcademicCalendar.value) return [];
  return teacher.value.lecturer_courses_in_semester.filter(
    course => course.academic_calendar_id === Number(selectedAcademicCalendar.value),
  );
});

// Allocate state
const showAllocateDialog = ref(false);
const targetLecturer = ref<{ id: number; name: string } | null>(null);
const selectedCourseId = ref<number | null>(null);
const allocating = ref(false);
const selectedCourses = ref<CourseLecturer[]>([]);
const bulkLoading = ref(false);

const { staff: staffData } = useStaff({ autoFetch: true });
const lecturers = computed(() =>
  staffData.data.value.map((s) => ({ id: Number(s.id), name: s.name })),
);

const openAllocateDialog = (courseId: number) => {
  selectedCourseId.value = courseId;
  showAllocateDialog.value = true;
};

const { mutate: allocateMutate } = useAllocateCoursesToLecturerMutation();

const handleAllocate = () => {
  if (!selectedCourseId.value || !targetLecturer.value) return;
  allocating.value = true;

  allocateMutate({
    input: {
      lecturer_id: targetLecturer.value.id,
      course_ids: [selectedCourseId.value],
      academic_calendar_id: Number(selectedAcademicCalendar.value),
    },
  })
    .then(() => {
      toast.add({ severity: 'success', summary: 'Allocated', detail: 'Course allocated successfully', life: 3000 });
      refetch();
      showAllocateDialog.value = false;
      targetLecturer.value = null;
    })
    .catch((err) => {
      toast.add({ severity: 'error', summary: 'Allocation Failed', detail: err instanceof Error ? err.message : 'Could not allocate course', life: 4000 });
    })
    .finally(() => (allocating.value = false));
};

// Confirm state
const showConfirmDialog = ref(false);
const confirmAction = ref<'approve' | 'deny' | 'deallocate' | null>(null);
const confirmCourseId = ref<number | null>(null);

const confirmActionText = computed(() => confirmAction.value ?? '');

const openConfirmDialog = (courseId: number, action: 'approve' | 'deny' | 'deallocate') => {
  confirmCourseId.value = courseId;
  confirmAction.value = action;
  showConfirmDialog.value = true;
};

const { mutate: approveOrDeny, loading: actionLoading, onDone, onError } = useApproveOrDenyCourseMutation();

onDone(() => {
  toast.add({ severity: 'success', summary: 'Success', detail: 'Course status updated', life: 3000 });
  refetch();
});
onError((err) => {
  toast.add({ severity: 'error', summary: 'Error', detail: err.message, life: 4000 });
});

const updateCourseStatus = (courseId: number, approve: boolean) => {
  if (!teacher.value) return;
  approveOrDeny({
    input: {
      course_ids: [Number(courseId)],
      lecturer_id: Number(teacher.value.id),
      approve,
      academic_calendar_id: Number(selectedAcademicCalendar.value),
    },
  });
};

async function bulkAction(approve: boolean) {
  if (!teacher.value || !selectedAcademicCalendar.value || !selectedCourses.value.length) return;
  bulkLoading.value = true;
  try {
    const courseIds = selectedCourses.value.map((c) => Number(c.course_id));
    await approveOrDeny({
      input: {
        course_ids: courseIds,
        lecturer_id: Number(teacher.value.id),
        approve,
        academic_calendar_id: Number(selectedAcademicCalendar.value),
      },
    });
    selectedCourses.value = [];
  } catch (err) {
    // onError handler already shows toast
  } finally {
    bulkLoading.value = false;
  }
}

// Assign courses
const showAssignDialog = ref(false);
const assignSelectedIds = ref<number[]>([]);
const assigning = ref(false);
const courseSearchQuery = ref('');

const { load: loadCourses, result: courseSearchResult, loading: courseSearchLoading } = useLazyQuery(
  GET_COURSES,
  undefined,
  { fetchPolicy: 'network-only' },
);

const availableCourses = computed(() => courseSearchResult.value?.listSchoolCourses?.data ?? []);

const debouncedCourseSearch = debounce(() => {
  if (courseSearchQuery.value.trim().length >= 2) {
    loadCourses(undefined, { first: 50, page: 1, filter: { query: courseSearchQuery.value.trim() } });
  }
}, 300);

watch(courseSearchQuery, () => debouncedCourseSearch());
watch(showAssignDialog, (open) => {
  if (!open) {
    assignSelectedIds.value = [];
    courseSearchQuery.value = '';
  }
});

async function handleAssignCourses() {
  if (!teacher.value || !selectedAcademicCalendar.value || !assignSelectedIds.value.length) return;
  assigning.value = true;
  try {
    await allocateMutate({
      input: {
        lecturer_id: Number(teacher.value.id),
        course_ids: assignSelectedIds.value,
        academic_calendar_id: Number(selectedAcademicCalendar.value),
      },
    });
    toast.add({ severity: 'success', summary: 'Assigned', detail: `${assignSelectedIds.value.length} course(s) assigned`, life: 3000 });
    showAssignDialog.value = false;
    refetch();
  } catch (err) {
    toast.add({ severity: 'error', summary: 'Failed', detail: err instanceof Error ? err.message : 'Could not assign courses', life: 4000 });
  } finally {
    assigning.value = false;
  }
}

const { mutate: deallocateMutate } = useDeallocateCourseFromLecturerMutation();

const deallocateCourse = (courseId: number) => {
  if (!teacher.value || !selectedAcademicCalendar.value) return;
  allocating.value = true;

  deallocateMutate({
    input: {
      lecturer_id: Number(teacher.value.id),
      course_ids: [courseId],
      academic_calendar_id: Number(selectedAcademicCalendar.value),
    },
  })
    .then(() => {
      toast.add({ severity: 'success', summary: 'Deallocated', detail: 'Course removed successfully', life: 3000 });
      refetch();
    })
    .catch((err) => {
      toast.add({ severity: 'error', summary: 'Deallocation Failed', detail: err instanceof Error ? err.message : 'Could not remove course', life: 4000 });
    })
    .finally(() => (allocating.value = false));
};

const handleConfirmAction = () => {
  if (!confirmCourseId.value || !confirmAction.value) return;
  showConfirmDialog.value = false;

  if (confirmAction.value === 'approve') updateCourseStatus(confirmCourseId.value, true);
  else if (confirmAction.value === 'deny') updateCourseStatus(confirmCourseId.value, false);
  else if (confirmAction.value === 'deallocate') deallocateCourse(confirmCourseId.value);

  confirmCourseId.value = null;
  confirmAction.value = null;
};
</script>
